import { useEffect, lazy } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "react-hot-toast";
import { I18nextProvider } from "react-i18next";
import i18n from "./lib/i18n";

// Components - Keep layout components as regular imports since they're used frequently
import Header from "./components/layout/Header";
import Footer from "./components/layout/Footer";
import LazyWrapper from "./components/ui/LazyWrapper";
import PageTransition from "./components/ui/PageTransition";
// Commented out with its render below (search "Back-to-top"): `noUnusedLocals`
// is on, so an unused import would fail the build. Restore both lines together.
// import BackToTop from "./components/ui/BackToTop";
import ScrollToTop from "./components/ui/ScrollToTop";
// Parked 2026-08-29 at the client's request: the public Services pages are hidden
// until they decide what to show. To restore, uncomment these two imports, the
// /services and /services/:id routes below, and the entry points listed in the
// route comment. Both pages and their locale copy are untouched on disk.
// (noUnusedLocals is on, so neither import can stay while the routes are redirects.)
// import ServicesPage from "./pages/ServicesPage"; // Regular import to avoid double loading

// Lazy load pages for better code splitting
const HomePage = lazy(() => import("./pages/HomePage"));
// const ServiceDetailPage = lazy(() => import("./pages/ServiceDetailPage"));
const SDGPage = lazy(() => import("./pages/SDGPage"));
const EQSPage = lazy(() => import("./pages/EQSPage"));
// Parked 2026-08-07: the Partners page is hidden until the business decides what
// to do with it. To restore, uncomment this, the /partners route below, and the
// nav/footer links in components/layout/{Header,Footer}.tsx. The page and its
// locales/{en,ar}/partners.json copy are untouched on disk.
// (noUnusedLocals is on, so this import cannot stay while the route is a redirect.)
// const PartnersPage = lazy(() => import("./pages/PartnersPage"));
const MarketplacePage = lazy(() => import("./pages/MarketplacePage"));
const MarketplaceDetailPage = lazy(() => import("./pages/MarketplaceDetailPage"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const ContactProfile = lazy(() => import("./pages/ContactProfile"));
const CartPage = lazy(() => import("./pages/CartPage"));
const OrderSuccessPage = lazy(() => import("./pages/OrderSuccessPage"));
const AuthPage = lazy(() => import("./pages/AuthPage"));
const ForgotPasswordPage = lazy(() => import("./pages/ForgotPasswordPage"));
const PublicSpecsPreview = lazy(() => import("./pages/PublicSpecsPreview"));
const PartnerPublicPage = lazy(() => import("./pages/PartnerPublicPage"));

// Lazy load customer components
const CustomerRoute = lazy(() => import("./components/customer/CustomerRoute"));
const CustomerOverviewPage = lazy(() => import("./pages/customer/OverviewPage"));
const CustomerOrders = lazy(() => import("./pages/CustomerOrders"));
const CustomerServiceInquiries = lazy(
  () => import("./pages/CustomerServiceInquiries"),
);
const CustomerProfile = lazy(() => import("./pages/CustomerProfile"));
// BroxSuite AI is ONE page as of 2026-08-27 - the rail routes, the pane holds
// the selected tool. It replaced CustomerAiToolkit/CustomerAiSpecs/
// CustomerAiComply, all three of which are gone.
const CustomerAiWorkspace = lazy(() => import("./pages/CustomerAiWorkspace"));
// Phase 4. Named CustomerProjectsPage at the import, not ProjectsPage: this file
// already lazy-loads a ProjectsPage for the CORPORATE portfolio on the public
// site, and the two are unrelated.
const CustomerProjectsPage = lazy(() => import("./pages/customer/ProjectsPage"));
const CustomerServicesPage = lazy(() => import("./pages/customer/ServicesPage"));
const CustomerMarketplacePage = lazy(() => import("./pages/customer/MarketplacePage"));
const CustomerProductDetailPage = lazy(() => import("./pages/customer/ProductDetailPage"));
const CustomerRfqsPage = lazy(() => import("./pages/customer/RfqsPage"));
// Phase 6. The customer's own URS documents, replacing the stub that stood at
// /dashboard/documents. Named CustomerDocumentsPage at the import for the same
// reason CustomerProjectsPage is: the PARTNER portal already has a
// DocumentsPage (its quality certificates), and the two are unrelated.
const CustomerDocumentsPage = lazy(() => import("./pages/customer/DocumentsPage"));
const CustomerRfqDetailPage = lazy(() => import("./pages/customer/RfqDetailPage"));
// Phase 5. The Demo Experience shell, stepper and step 1 (Task 4).
const CustomerDemoPage = lazy(() => import("./pages/customer/DemoPage"));
// Phase 5. The Tender Center shell, preview banner, role switch, stats and
// portfolio (Task 7). Task 8 adds the twelve-stage workspace to this same page.
const CustomerTenderCenterPage = lazy(() => import("./pages/customer/TenderCenterPage"));

// Lazy load admin components
const AdminLogin = lazy(() => import("./components/admin/AdminLogin"));
const AdminRoute = lazy(() => import("./components/admin/AdminRoute"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const AdminOrders = lazy(() => import("./pages/AdminOrders"));
const AdminUsers = lazy(() => import("./pages/AdminUsers"));
const AdminServices = lazy(() => import("./pages/AdminServices"));
const AdminServiceCatalog = lazy(() => import("./pages/AdminServiceCatalog"));
const AdminEquipment = lazy(() => import("./pages/AdminEquipment"));
const AdminCategories = lazy(() => import("./pages/AdminCategories"));
const AdminSuppliers = lazy(() => import("./pages/AdminSuppliers"));
const AdminCodes = lazy(() => import("./pages/AdminCodes"));
const AdminContacts = lazy(() => import("./pages/AdminContacts"));
const AdminLoginLogs = lazy(() => import("./pages/AdminLoginLogs"));
const AdminEmailLogs = lazy(() => import("./pages/AdminEmailLogs"));
const AdminAnnouncements = lazy(() => import("./pages/AdminAnnouncements"));
const AdminEvents = lazy(() => import("./pages/AdminEvents"));
const AdminProjects = lazy(() => import("./pages/AdminProjects"));
const AdminOrderSteps = lazy(() => import("./pages/AdminOrderSteps"));
const AdminSupplierRequests = lazy(() => import("./pages/AdminSupplierRequests"));
const AdminProductRequests = lazy(() => import("./pages/AdminProductRequests"));
const AdminServiceRequestsPage = lazy(() => import("./pages/AdminServiceRequestsPage"));
const AdminRfqs = lazy(() => import("./pages/AdminRfqs"));
// Customer DELIVERY projects (Phase 4). NOT AdminProjects, which manages the
// CORPORATE reference-project portfolio for the public marketing site.
const AdminCustomerProjects = lazy(() => import("./pages/AdminCustomerProjects"));

// Public announcements page
const AnnouncementsPage = lazy(() => import("./pages/AnnouncementsPage"));

// Public events page
const EventsPage = lazy(() => import("./pages/EventsPage"));

// Public projects page
const ProjectsPage = lazy(() => import("./pages/Projects"));

// Public terms and conditions page
const TermsPage = lazy(() => import("./pages/TermsPage"));

// Public privacy policy page
const PrivacyPage = lazy(() => import("./pages/PrivacyPage"));

// Lazy load supplier portal components
const SupplierRoute = lazy(() => import("./components/supplier/SupplierRoute"));
const SupplierLogin = lazy(() => import("./pages/SupplierLogin"));
const SupplierRegister = lazy(() => import("./pages/SupplierRegister"));
const SupplierDashboard = lazy(() => import("./pages/SupplierDashboard"));
const SupplierProfile = lazy(() => import("./pages/SupplierProfile"));
const SupplierProducts = lazy(() => import("./pages/SupplierProducts"));
const SupplierProductForm = lazy(() => import("./pages/SupplierProductForm"));
// Portal routes that exist so the sidebar has no dead links; each is filled in
// by its own chunk. SupplierServicesPage is renamed at the import site because
// an unrelated public ServicesPage is already bound above.
const SupplierQualificationPage = lazy(() => import("./pages/supplier/QualificationPage"));
const SupplierDocumentsPage = lazy(() => import("./pages/supplier/DocumentsPage"));
const SupplierRfqsPage = lazy(() => import("./pages/supplier/RfqsPage"));
const SupplierProjectsPage = lazy(() => import("./pages/supplier/ProjectsPage"));
const SupplierServicesPage = lazy(() => import("./pages/supplier/ServicesPage"));

// Store
import { useLanguageStore } from "./store";

// The client itself lives in lib/queryClient.ts, NOT here. It used to be a
// module-local `const` on this line, which put it out of reach of the auth
// stores - so signing out cleared the token, the cart and the guest draft
// while leaving the previous person's responses cached for the next one.
// Exporting it from App.tsx was not an option: App.tsx already imports
// `../store`, so the store importing back would be a cycle.
import { queryClient } from "./lib/queryClient";

function App() {
  const { language } = useLanguageStore();

  // Set initial document direction based on language
  useEffect(() => {
    if (language === "ar") {
      document.documentElement.dir = "rtl";
      document.documentElement.lang = "ar";
    } else {
      document.documentElement.dir = "ltr";
      document.documentElement.lang = "en";
    }
    i18n.changeLanguage(language);
  }, [language]);

  return (
    <QueryClientProvider client={queryClient}>
      <I18nextProvider i18n={i18n}>
        <Router
          future={{ v7_startTransition: true, v7_relativeSplatPath: true }}
        >
          <ScrollToTop />
          <Routes>
            {/* Public Routes */}
            <Route
              path="/"
              element={
                <PageTransition className="min-h-screen flex flex-col bg-background text-foreground">
                  <Header />
                  <main className="flex-1">
                    <LazyWrapper>
                      <HomePage />
                    </LazyWrapper>
                  </main>
                  <Footer />
                </PageTransition>
              }
            />
            {/*
              Services pages parked 2026-08-29 at the client's request. Kept as
              redirects rather than deleted: this app has no 404 catch-all, so
              removing the routes outright would serve a blank page to every
              bookmark, stale link and search-engine hit. react-router v6 matches
              path="/services" exactly, so both patterns need their own redirect.

              Entry points removed alongside these (restore them together):
                - components/layout/Header.tsx      nav item
                - components/layout/Footer.tsx      quick-links item
                - pages/HomePage.tsx                <ServicesSection />
                - components/homepage/CTASection.tsx      "Explore Our Services"
                - components/homepage/ProcessSection.tsx  CTA repointed to /contact
                - pages/AboutPage.tsx               hero CTA, 3 pillar links, footer CTA
                - pages/EQSPage.tsx                 hero CTA, footer CTA
                - components/sdg/SdgCta.tsx         secondary CTA
                - pages/Projects.tsx                related-service chips unlinked
              The backend /api/services routes and the admin Services screen are
              untouched - only the public pages are hidden.
            */}
            <Route path="/services" element={<Navigate to="/" replace />} />
            <Route path="/services/:id" element={<Navigate to="/" replace />} />
            <Route
              path="/sdg"
              element={
                <PageTransition className="min-h-screen flex flex-col bg-background text-foreground">
                  <Header />
                  <main className="flex-1">
                    <LazyWrapper>
                      <SDGPage />
                    </LazyWrapper>
                  </main>
                  <Footer />
                </PageTransition>
              }
            />
            <Route
              path="/eqs"
              element={
                <PageTransition className="min-h-screen flex flex-col bg-background text-foreground">
                  <Header />
                  <main className="flex-1">
                    <LazyWrapper>
                      <EQSPage />
                    </LazyWrapper>
                  </main>
                  <Footer />
                </PageTransition>
              }
            />
            {/*
              Partners page parked 2026-08-07. Kept as a redirect rather than
              deleted: this app has no 404 catch-all, so removing the route
              outright would serve a blank page to every bookmark, stale link and
              search-engine hit. Restore by swapping this back for the original
              PageTransition/Header/PartnersPage/Footer block (see the commented
              lazy import at the top of this file).
            */}
            <Route path="/partners" element={<Navigate to="/" replace />} />
            {/*
              The partner PAGE, distinct from the parked /partners directory above.
              react-router v6 matches path="/partners" exactly, so the redirect does not
              shadow this route.
            */}
            <Route
              path="/partners/:id"
              element={
                <PageTransition className="min-h-screen flex flex-col bg-background text-foreground">
                  <Header />
                  <main className="flex-1">
                    <LazyWrapper>
                      <PartnerPublicPage />
                    </LazyWrapper>
                  </main>
                  <Footer />
                </PageTransition>
              }
            />
            <Route
              path="/terms"
              element={
                <PageTransition className="min-h-screen flex flex-col bg-background text-foreground">
                  <Header />
                  <main className="flex-1">
                    <LazyWrapper>
                      <TermsPage />
                    </LazyWrapper>
                  </main>
                  <Footer />
                </PageTransition>
              }
            />
            <Route
              path="/privacy"
              element={
                <PageTransition className="min-h-screen flex flex-col bg-background text-foreground">
                  <Header />
                  <main className="flex-1">
                    <LazyWrapper>
                      <PrivacyPage />
                    </LazyWrapper>
                  </main>
                  <Footer />
                </PageTransition>
              }
            />
            <Route
              path="/marketplace"
              element={
                <PageTransition className="min-h-screen flex flex-col bg-background text-foreground">
                  <Header />
                  <main className="flex-1">
                    <LazyWrapper>
                      <MarketplacePage />
                    </LazyWrapper>
                  </main>
                  <Footer />
                </PageTransition>
              }
            />
            <Route
              path="/marketplace/:id"
              element={
                <PageTransition className="min-h-screen flex flex-col bg-background text-foreground">
                  <Header />
                  <main className="flex-1">
                    <LazyWrapper>
                      <MarketplaceDetailPage />
                    </LazyWrapper>
                  </main>
                  <Footer />
                </PageTransition>
              }
            />
            <Route
              path="/cart"
              element={
                <PageTransition className="min-h-screen flex flex-col bg-background text-foreground">
                  <Header />
                  <main className="flex-1">
                    <LazyWrapper>
                      <CartPage />
                    </LazyWrapper>
                  </main>
                  <Footer />
                </PageTransition>
              }
            />
            <Route
              path="/order-success"
              element={
                <PageTransition className="min-h-screen flex flex-col bg-background text-foreground">
                  <Header />
                  <main className="flex-1">
                    <LazyWrapper>
                      <OrderSuccessPage />
                    </LazyWrapper>
                  </main>
                  <Footer />
                </PageTransition>
              }
            />
            <Route
              path="/about"
              element={
                <PageTransition className="min-h-screen flex flex-col bg-background text-foreground">
                  <Header />
                  <main className="flex-1">
                    <LazyWrapper>
                      <AboutPage />
                    </LazyWrapper>
                  </main>
                  <Footer />
                </PageTransition>
              }
            />
            <Route
              path="/contact"
              element={
                <PageTransition className="min-h-screen flex flex-col bg-background text-foreground">
                  <Header />
                  <main className="flex-1">
                    <LazyWrapper>
                      <ContactPage />
                    </LazyWrapper>
                  </main>
                  <Footer />
                </PageTransition>
              }
            />
            <Route
              path="/specs"
              element={
                <PageTransition className="min-h-screen flex flex-col bg-background text-foreground">
                  <Header />
                  <main className="flex-1">
                    <LazyWrapper>
                      <PublicSpecsPreview />
                    </LazyWrapper>
                  </main>
                  <Footer />
                </PageTransition>
              }
            />
            <Route
              path="/contact-card"
              element={
                <PageTransition className="min-h-screen bg-background text-foreground">
                  <LazyWrapper>
                    <ContactProfile />
                  </LazyWrapper>
                </PageTransition>
              }
            />
            <Route
              path="/announcements"
              element={
                <PageTransition className="min-h-screen flex flex-col bg-background text-foreground">
                  <Header />
                  <main className="flex-1">
                    <LazyWrapper>
                      <AnnouncementsPage />
                    </LazyWrapper>
                  </main>
                  <Footer />
                </PageTransition>
              }
            />
            <Route
              path="/events"
              element={
                <PageTransition className="min-h-screen flex flex-col bg-background text-foreground">
                  <Header />
                  <main className="flex-1">
                    <LazyWrapper>
                      <EventsPage />
                    </LazyWrapper>
                  </main>
                  <Footer />
                </PageTransition>
              }
            />
            <Route
              path="/projects"
              element={
                <PageTransition className="min-h-screen flex flex-col bg-background text-foreground">
                  <Header />
                  <main className="flex-1">
                    <LazyWrapper>
                      <ProjectsPage />
                    </LazyWrapper>
                  </main>
                  <Footer />
                </PageTransition>
              }
            />

            {/* Auth Routes */}
            <Route
              path="/login"
              element={
                <PageTransition>
                  <LazyWrapper>
                    <AuthPage />
                  </LazyWrapper>
                </PageTransition>
              }
            />
            <Route
              path="/forgot-password"
              element={
                <PageTransition>
                  <LazyWrapper>
                    <ForgotPasswordPage audience="customer" />
                  </LazyWrapper>
                </PageTransition>
              }
            />

            {/* Customer Dashboard Routes */}
            <Route
              path="/dashboard"
              element={
                <PageTransition>
                  <LazyWrapper>
                    <CustomerRoute>
                      <CustomerOverviewPage />
                    </CustomerRoute>
                  </LazyWrapper>
                </PageTransition>
              }
            />
            <Route
              path="/dashboard/orders"
              element={
                <PageTransition>
                  <LazyWrapper>
                    <CustomerRoute>
                      <CustomerOrders />
                    </CustomerRoute>
                  </LazyWrapper>
                </PageTransition>
              }
            />
            <Route
              path="/dashboard/service-inquiries"
              element={
                <PageTransition>
                  <LazyWrapper>
                    <CustomerRoute>
                      <CustomerServiceInquiries />
                    </CustomerRoute>
                  </LazyWrapper>
                </PageTransition>
              }
            />
            <Route
              path="/dashboard/profile"
              element={
                <PageTransition>
                  <LazyWrapper>
                    <CustomerRoute>
                      <CustomerProfile />
                    </CustomerRoute>
                  </LazyWrapper>
                </PageTransition>
              }
            />
            {/* BroxSuite AI is ONE page. `/dashboard/ai` resolves to the first
                LIVE tool; `/dashboard/ai/:tool` selects one by key, and an
                unrecognised key redirects (there is no 404 catch-all in this
                app, so a blank white screen is the alternative).

                /dashboard/ai/specs and /dashboard/ai/comply still resolve, and
                that is not optional: SpecsDraftCard, SpecsDraftModal,
                SourcingRail, RfqDetailPage and DocumentsPage all link to the
                first of them, and PublicSpecsPreview redirects to it the moment
                a guest registers. The :tool param route is what keeps them
                working - so it must never be narrowed to a fixed list. */}
            <Route
              path="/dashboard/ai"
              element={
                <PageTransition>
                  <LazyWrapper>
                    <CustomerRoute>
                      <CustomerAiWorkspace />
                    </CustomerRoute>
                  </LazyWrapper>
                </PageTransition>
              }
            />
            <Route
              path="/dashboard/ai/:tool"
              element={
                <PageTransition>
                  <LazyWrapper>
                    <CustomerRoute>
                      <CustomerAiWorkspace />
                    </CustomerRoute>
                  </LazyWrapper>
                </PageTransition>
              }
            />
            <Route
              path="/dashboard/demo"
              element={
                <PageTransition>
                  <LazyWrapper>
                    <CustomerRoute>
                      <CustomerDemoPage />
                    </CustomerRoute>
                  </LazyWrapper>
                </PageTransition>
              }
            />
            <Route
              path="/dashboard/marketplace"
              element={
                <PageTransition>
                  <LazyWrapper>
                    <CustomerRoute>
                      <CustomerMarketplacePage />
                    </CustomerRoute>
                  </LazyWrapper>
                </PageTransition>
              }
            />
            {/* One product's detail page. activeCustomerNavKey needs no change
                for it: it already matches `clean === item.path ||
                clean.startsWith(item.path + '/')`, so this path lights
                Marketplace on the rail, and the required trailing slash is
                what stops a sibling like /dashboard/marketplace-archive from
                doing the same. */}
            <Route
              path="/dashboard/marketplace/:id"
              element={
                <PageTransition>
                  <LazyWrapper>
                    <CustomerRoute>
                      <CustomerProductDetailPage />
                    </CustomerRoute>
                  </LazyWrapper>
                </PageTransition>
              }
            />
            <Route
              path="/dashboard/rfqs"
              element={
                <PageTransition>
                  <LazyWrapper>
                    <CustomerRoute>
                      <CustomerRfqsPage />
                    </CustomerRoute>
                  </LazyWrapper>
                </PageTransition>
              }
            />
            <Route
              path="/dashboard/rfqs/:id"
              element={
                <PageTransition>
                  <LazyWrapper>
                    <CustomerRoute>
                      <CustomerRfqDetailPage />
                    </CustomerRoute>
                  </LazyWrapper>
                </PageTransition>
              }
            />
            <Route
              path="/dashboard/tenders"
              element={
                <PageTransition>
                  <LazyWrapper>
                    <CustomerRoute>
                      <CustomerTenderCenterPage />
                    </CustomerRoute>
                  </LazyWrapper>
                </PageTransition>
              }
            />
            <Route
              path="/dashboard/projects"
              element={
                <PageTransition>
                  <LazyWrapper>
                    <CustomerRoute>
                      <CustomerProjectsPage />
                    </CustomerRoute>
                  </LazyWrapper>
                </PageTransition>
              }
            />
            <Route
              path="/dashboard/services"
              element={
                <PageTransition>
                  <LazyWrapper>
                    <CustomerRoute>
                      <CustomerServicesPage />
                    </CustomerRoute>
                  </LazyWrapper>
                </PageTransition>
              }
            />
            <Route
              path="/dashboard/documents"
              element={
                <PageTransition>
                  <LazyWrapper>
                    <CustomerRoute>
                      <CustomerDocumentsPage />
                    </CustomerRoute>
                  </LazyWrapper>
                </PageTransition>
              }
            />

            {/* Admin Routes */}
            <Route
              path="/admin/login"
              element={
                <PageTransition>
                  <LazyWrapper>
                    <AdminLogin />
                  </LazyWrapper>
                </PageTransition>
              }
            />
            <Route
              path="/admin"
              element={
                <PageTransition>
                  <LazyWrapper>
                    <AdminRoute>
                      <AdminDashboard />
                    </AdminRoute>
                  </LazyWrapper>
                </PageTransition>
              }
            />
            <Route
              path="/admin/orders"
              element={
                <PageTransition>
                  <LazyWrapper>
                    <AdminRoute>
                      <AdminOrders />
                    </AdminRoute>
                  </LazyWrapper>
                </PageTransition>
              }
            />
            <Route
              path="/admin/order-steps"
              element={
                <PageTransition>
                  <LazyWrapper>
                    <AdminRoute>
                      <AdminOrderSteps />
                    </AdminRoute>
                  </LazyWrapper>
                </PageTransition>
              }
            />
            <Route
              path="/admin/users"
              element={
                <PageTransition>
                  <LazyWrapper>
                    <AdminRoute>
                      <AdminUsers />
                    </AdminRoute>
                  </LazyWrapper>
                </PageTransition>
              }
            />
            <Route
              path="/admin/services"
              element={
                <PageTransition>
                  <LazyWrapper>
                    <AdminRoute>
                      <AdminServices />
                    </AdminRoute>
                  </LazyWrapper>
                </PageTransition>
              }
            />
            <Route
              path="/admin/service-catalog"
              element={
                <PageTransition>
                  <LazyWrapper>
                    <AdminRoute>
                      <AdminServiceCatalog />
                    </AdminRoute>
                  </LazyWrapper>
                </PageTransition>
              }
            />
            <Route
              path="/admin/equipment"
              element={
                <PageTransition>
                  <LazyWrapper>
                    <AdminRoute>
                      <AdminEquipment />
                    </AdminRoute>
                  </LazyWrapper>
                </PageTransition>
              }
            />
            <Route
              path="/admin/categories"
              element={
                <PageTransition>
                  <LazyWrapper>
                    <AdminRoute>
                      <AdminCategories />
                    </AdminRoute>
                  </LazyWrapper>
                </PageTransition>
              }
            />
            <Route
              path="/admin/suppliers"
              element={
                <PageTransition>
                  <LazyWrapper>
                    <AdminRoute>
                      <AdminSuppliers />
                    </AdminRoute>
                  </LazyWrapper>
                </PageTransition>
              }
            />
            <Route
              path="/admin/supplier-requests"
              element={
                <PageTransition>
                  <LazyWrapper>
                    <AdminRoute>
                      <AdminSupplierRequests />
                    </AdminRoute>
                  </LazyWrapper>
                </PageTransition>
              }
            />
            <Route
              path="/admin/product-requests"
              element={
                <PageTransition>
                  <LazyWrapper>
                    <AdminRoute>
                      <AdminProductRequests />
                    </AdminRoute>
                  </LazyWrapper>
                </PageTransition>
              }
            />
            <Route
              path="/admin/service-requests"
              element={
                <PageTransition>
                  <LazyWrapper>
                    <AdminRoute>
                      <AdminServiceRequestsPage />
                    </AdminRoute>
                  </LazyWrapper>
                </PageTransition>
              }
            />
            <Route
              path="/admin/rfqs"
              element={
                <PageTransition>
                  <LazyWrapper>
                    <AdminRoute>
                      <AdminRfqs />
                    </AdminRoute>
                  </LazyWrapper>
                </PageTransition>
              }
            />
            {/* `/admin/customer-projects`, NOT `/admin/projects` - that path is
                already the corporate portfolio screen. Two unrelated things; the
                longer path removes a whole class of confusion. */}
            <Route
              path="/admin/customer-projects"
              element={
                <PageTransition>
                  <LazyWrapper>
                    <AdminRoute>
                      <AdminCustomerProjects />
                    </AdminRoute>
                  </LazyWrapper>
                </PageTransition>
              }
            />
            <Route
              path="/admin/codes"
              element={
                <PageTransition>
                  <LazyWrapper>
                    <AdminRoute>
                      <AdminCodes />
                    </AdminRoute>
                  </LazyWrapper>
                </PageTransition>
              }
            />
            <Route
              path="/admin/contacts"
              element={
                <PageTransition>
                  <LazyWrapper>
                    <AdminRoute>
                      <AdminContacts />
                    </AdminRoute>
                  </LazyWrapper>
                </PageTransition>
              }
            />
            <Route
              path="/admin/login-logs"
              element={
                <PageTransition>
                  <LazyWrapper>
                    <AdminRoute>
                      <AdminLoginLogs />
                    </AdminRoute>
                  </LazyWrapper>
                </PageTransition>
              }
            />
            <Route
              path="/admin/email-logs"
              element={
                <PageTransition>
                  <LazyWrapper>
                    <AdminRoute>
                      <AdminEmailLogs />
                    </AdminRoute>
                  </LazyWrapper>
                </PageTransition>
              }
            />
            <Route
              path="/admin/events"
              element={
                <PageTransition>
                  <LazyWrapper>
                    <AdminRoute>
                      <AdminEvents />
                    </AdminRoute>
                  </LazyWrapper>
                </PageTransition>
              }
            />
            <Route
              path="/admin/projects"
              element={
                <PageTransition>
                  <LazyWrapper>
                    <AdminRoute>
                      <AdminProjects />
                    </AdminRoute>
                  </LazyWrapper>
                </PageTransition>
              }
            />
            <Route
              path="/admin/announcements"
              element={
                <PageTransition>
                  <LazyWrapper>
                    <AdminRoute>
                      <AdminAnnouncements />
                    </AdminRoute>
                  </LazyWrapper>
                </PageTransition>
              }
            />

            {/* Supplier Portal Routes */}
            <Route path="/supplier/register" element={
              <PageTransition><LazyWrapper><SupplierRegister /></LazyWrapper></PageTransition>
            } />
            <Route path="/supplier/login" element={
              <PageTransition><LazyWrapper><SupplierLogin /></LazyWrapper></PageTransition>
            } />
            <Route path="/supplier/forgot-password" element={
              <PageTransition><LazyWrapper><ForgotPasswordPage audience="partner" /></LazyWrapper></PageTransition>
            } />
            <Route path="/supplier" element={
              <PageTransition><LazyWrapper><SupplierRoute><SupplierDashboard /></SupplierRoute></LazyWrapper></PageTransition>
            } />
            <Route path="/supplier/profile" element={
              <PageTransition><LazyWrapper><SupplierRoute><SupplierProfile /></SupplierRoute></LazyWrapper></PageTransition>
            } />
            <Route path="/supplier/products" element={
              <PageTransition><LazyWrapper><SupplierRoute><SupplierProducts /></SupplierRoute></LazyWrapper></PageTransition>
            } />
            <Route path="/supplier/products/new" element={
              <PageTransition><LazyWrapper><SupplierRoute><SupplierProductForm /></SupplierRoute></LazyWrapper></PageTransition>
            } />
            <Route path="/supplier/products/:id/edit" element={
              <PageTransition><LazyWrapper><SupplierRoute><SupplierProductForm /></SupplierRoute></LazyWrapper></PageTransition>
            } />
            <Route path="/supplier/qualification" element={
              <PageTransition><LazyWrapper><SupplierRoute><SupplierQualificationPage /></SupplierRoute></LazyWrapper></PageTransition>
            } />
            <Route path="/supplier/documents" element={
              <PageTransition><LazyWrapper><SupplierRoute><SupplierDocumentsPage /></SupplierRoute></LazyWrapper></PageTransition>
            } />
            <Route path="/supplier/rfqs" element={
              <PageTransition><LazyWrapper><SupplierRoute><SupplierRfqsPage /></SupplierRoute></LazyWrapper></PageTransition>
            } />
            <Route path="/supplier/projects" element={
              <PageTransition><LazyWrapper><SupplierRoute><SupplierProjectsPage /></SupplierRoute></LazyWrapper></PageTransition>
            } />
            <Route path="/supplier/services" element={
              <PageTransition><LazyWrapper><SupplierRoute><SupplierServicesPage /></SupplierRoute></LazyWrapper></PageTransition>
            } />
          </Routes>
          {/* Back-to-top button HIDDEN at the owner's request (2026-08-18) —
              it was getting in the way, most visibly overlapping the partner
              name in the portal sidebar's footer. Commented out rather than
              deleted so restoring it means uncommenting this line AND the
              import at the top of the file; the component itself is untouched. */}
          {/* <BackToTop /> */}
          <Toaster
            position="top-right"
            toastOptions={{
              duration: 4000,
              style: {
                background: "hsl(var(--background))",
                color: "hsl(var(--foreground))",
                border: "1px solid hsl(var(--border))",
              },
            }}
          />
        </Router>
      </I18nextProvider>
    </QueryClientProvider>
  );
}

export default App;
